import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "@/components/ui/dialog";
import { CheckIcon, MailIcon, MessageSquareIcon } from "lucide-react";
import { socialsFor } from "./agent-persona.js";
import { kitFor } from "./agent-kit.js";
import { AgentAvatar } from "./AgentAvatar.js";
import type { PlatformConnection } from "./connections-api.js";
import { loadFirmKit } from "./firm-kit.js";
import { PlatformMark } from "./PlatformMark.js";
import { agentById, type AgentId } from "./nav.js";
import { cn } from "./cn.js";

type Props = {
	agentId: AgentId | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onMessage?: () => void;
	/** Live rows from GET /api/connections — drives connector Connected/Missing. */
	providers?: readonly PlatformConnection[];
};

function XMark({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			aria-hidden
			className={className}
			fill="currentColor"
		>
			<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
		</svg>
	);
}

/** Artist-style agent profile — settings-scale overlay that fits every viewport. */
export function AgentInfoModal({
	agentId,
	open,
	onOpenChange,
	onMessage,
	providers,
}: Props) {
	if (!agentId) return null;

	const agent = agentById(agentId);
	const kit = kitFor(agentId, providers);
	const socials = socialsFor(agentId);
	const firm = loadFirmKit().find((row) => row.agentId === agentId);
	const email =
		firm?.channels.find((c) => c.channel === "email")?.identity?.trim() ||
		null;
	const hasFollows = Boolean(socials.linkedinUrl || socials.xUrl || email);

	const message = () => {
		onMessage?.();
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton
				className="agent-persona-modal gap-0 overflow-hidden p-0"
				overlayClassName="agent-persona-overlay"
			>
				<div className="agent-persona-layout">
					<aside className="agent-persona-aside">
						<div className="agent-persona-portrait-wrap">
							<div className="agent-persona-portrait">
								<AgentAvatar agent={agent} size="xl" />
							</div>
						</div>
						<div className="agent-persona-aside-copy">
							<p className="agent-persona-dept">{agent.department}</p>
							<DialogTitle className="agent-persona-name">
								{agent.name}
							</DialogTitle>
							<p className="agent-persona-title">{agent.title}</p>
						</div>
						{hasFollows ? (
							<div className="agent-persona-follows" aria-label="Follow">
								{socials.linkedinUrl ? (
									<a
										className="agent-persona-follow"
										href={socials.linkedinUrl}
										target="_blank"
										rel="noreferrer"
									>
										<PlatformMark
											markId="linkedin"
											size={14}
											fallback="in"
										/>
										LinkedIn
									</a>
								) : null}
								{socials.xUrl ? (
									<a
										className="agent-persona-follow"
										href={socials.xUrl}
										target="_blank"
										rel="noreferrer"
									>
										<XMark className="size-3.5" />
										X
									</a>
								) : null}
								{email ? (
									<a
										className="agent-persona-follow"
										href={`mailto:${email}`}
									>
										<MailIcon className="size-3.5" />
										Email
									</a>
								) : null}
							</div>
						) : null}
						{onMessage ? (
							<Button
								type="button"
								className="agent-persona-cta agent-persona-cta--aside"
								onClick={message}
							>
								<MessageSquareIcon className="size-4" />
								Message {agent.name}
							</Button>
						) : null}
					</aside>

					<div className="agent-persona-main">
						<section className="agent-persona-card">
							<h3 className="agent-persona-label">About</h3>
							<DialogDescription className="agent-persona-bio">
								{kit.job}
							</DialogDescription>
							{agent.lede ? (
								<p className="agent-persona-lede">{agent.lede}</p>
							) : null}
						</section>

						<section
							className="agent-persona-card"
							aria-labelledby="agent-works-with"
						>
							<h3 id="agent-works-with" className="agent-persona-label">
								Works with
							</h3>
							<ul className="agent-persona-pills">
								{kit.connectors.map((c) => (
									<li
										key={c.id}
										className={cn(
											"agent-persona-pill",
											c.state === "connected" &&
												"agent-persona-pill--live",
											c.state === "soon" &&
												"agent-persona-pill--soon",
										)}
										title={`${c.name} · ${c.required ? "Required" : "Optional"}`}
									>
										<PlatformMark
											markId={c.markId}
											size={14}
											fallback={c.name}
										/>
										<span>{c.name}</span>
									</li>
								))}
							</ul>
						</section>

						<section
							className="agent-persona-card"
							aria-labelledby="agent-capabilities"
						>
							<h3
								id="agent-capabilities"
								className="agent-persona-label"
							>
								Capabilities
							</h3>
							<ul className="agent-persona-caps">
								{kit.tools.map((tool) => (
									<li key={tool.id}>
										<CheckIcon
											className="agent-persona-cap-check"
											aria-hidden
										/>
										<span>{tool.label}</span>
									</li>
								))}
							</ul>
						</section>
					</div>
				</div>

				{onMessage ? (
					<div className="agent-persona-foot">
						<Button
							type="button"
							className="agent-persona-cta"
							onClick={message}
						>
							<MessageSquareIcon className="size-4" />
							Message {agent.name}
						</Button>
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	);
}
